// Inline vendor typeahead for material lines that have no vendor yet. Vendors must exist in Settings → Vendors.
import { useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

export function VendorPicker({ manufacturers, saving, onSelect, testIdSuffix }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [openDirection, setOpenDirection] = useState("down");
  const inputWrapRef = useRef(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return manufacturers.slice(0, 8);
    return manufacturers.filter(m => (m.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [manufacturers, query]);

  const handleFocus = () => {
    setFocused(true);
    const node = inputWrapRef.current;
    if (node) {
      const rect = node.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenDirection(spaceBelow < 240 && rect.top > spaceBelow ? "up" : "down");
    }
  };

  const exactMatch = manufacturers.some(m => (m.name || "").toLowerCase() === query.trim().toLowerCase());
  const typedNew = query.trim().length > 0 && !exactMatch;

  return (
    <div className="relative" ref={inputWrapRef}>
      <Input value={query} onChange={e => setQuery(e.target.value)} onFocus={handleFocus} onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder="Assign vendor..." className="h-7 text-xs" disabled={saving} data-testid={`bom-vendor-input-${testIdSuffix}`} />
      {focused && (matches.length > 0 || typedNew) && (
        <div className={`absolute z-[9999] left-0 min-w-[280px] ${openDirection === "up" ? "bottom-full mb-0.5" : "top-full mt-0.5"} bg-white border rounded-lg shadow-xl max-h-64 overflow-y-auto text-xs`} data-testid={`bom-vendor-menu-${testIdSuffix}`}>
          {matches.map(m => (
            <button key={m.id || m.name} type="button" className="block w-full text-left px-2 py-1.5 hover:bg-slate-50 truncate"
              onMouseDown={(e) => { e.preventDefault(); setQuery(""); onSelect(m.name); }} data-testid={`bom-vendor-opt-${testIdSuffix}-${m.id || m.name}`}>
              <span className="font-medium text-slate-700">{m.name}</span>
              {m.purchasing_contact_email && <span className="ml-1 text-[10px] text-slate-400">{m.purchasing_contact_email}</span>}
            </button>
          ))}
          {typedNew && matches.length === 0 && (
            <div className="px-2 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-200" data-testid={`bom-vendor-not-found-${testIdSuffix}`}>
              No vendor matches “{query.trim()}”. Add it under <span className="font-semibold">Settings → Vendors</span> first.
            </div>
          )}
          {typedNew && matches.length > 0 && <div className="px-2 py-1.5 text-[10px] text-slate-400 border-t">Pick from the list — vendors must be set up in Settings first.</div>}
        </div>
      )}
    </div>
  );
}
